import { Controller } from "@hotwired/stimulus";

const TICK_MS = 250;

let documentTimer: number | null = null;

type SetIntervalFn = (
  handler: TimerHandler,
  timeout?: number,
  ...args: unknown[]
) => number;

function startDocumentTicker(): void {
  if (documentTimer !== null) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;

  updateThinkingIndicators(document);
  // Auto-refresh clears tracked intervals after morphdom; this UI clock must
  // survive those swaps while the thinking indicator remains in the DOM.
  const setIntervalFn =
    (window as unknown as { unsafeSetInterval?: SetIntervalFn }).unsafeSetInterval ??
    window.setInterval.bind(window);
  documentTimer = setIntervalFn(tickDocumentIndicators, TICK_MS);
}

function stopDocumentTicker(): void {
  if (documentTimer === null) return;
  window.clearInterval(documentTimer);
  documentTimer = null;
}

function tickDocumentIndicators(): void {
  // Self-stop when the last indicator left the DOM (morphdom swaps never call
  // disconnect reliably mid-swap); the next connect() restarts the clock.
  if (!document.querySelector("[data-ticket-chat-thinking-indicator]")) {
    stopDocumentTicker();
    return;
  }
  updateThinkingIndicators(document);
}

function updateThinkingIndicators(root: ParentNode): void {
  for (const indicator of root.querySelectorAll<HTMLElement>(
    "[data-ticket-chat-thinking-indicator]",
  )) {
    updateThinkingIndicator(indicator);
  }
}

function updateThinkingIndicator(indicator: HTMLElement): void {
  const label = indicator.querySelector<HTMLElement>(
    "[data-ticket-chat-thinking-label]",
  );
  if (!label) return;

  const elapsedLabel = indicator.querySelector<HTMLElement>(
    "[data-ticket-chat-thinking-elapsed]",
  );
  const startedAtRaw = indicator.dataset.ticketChatThinkingStartedAt || "";
  const startedAtMs = Date.parse(startedAtRaw);
  if (!Number.isFinite(startedAtMs)) {
    setElapsedLabel(indicator, label, elapsedLabel, "");
    return;
  }

  const elapsedSeconds = Math.max(
    0,
    Math.floor((Date.now() - startedAtMs) / 1000),
  );
  setElapsedLabel(
    indicator,
    label,
    elapsedLabel,
    `(${formatElapsed(indicator, elapsedSeconds)})`,
  );
}

function setElapsedLabel(
  indicator: HTMLElement,
  label: HTMLElement,
  elapsedLabel: HTMLElement | null,
  text: string,
): void {
  if (elapsedLabel) {
    if (elapsedLabel.textContent !== text) {
      elapsedLabel.textContent = text;
    }
    return;
  }

  const baseLabel = indicator.dataset.ticketChatThinkingBaseLabel || "";
  const fallbackText = text ? `${baseLabel} ${text}`.trim() : baseLabel;
  if (label.textContent !== fallbackText) {
    label.textContent = fallbackText;
  }
}

function formatElapsed(indicator: HTMLElement, totalSeconds: number): string {
  const sinceLabel = indicator.dataset.ticketChatThinkingSinceLabel || "";
  const secondLabel = indicator.dataset.ticketChatThinkingSecondLabel || "s";
  const secondsLabel = indicator.dataset.ticketChatThinkingSecondsLabel || secondLabel;
  const minuteLabel = indicator.dataset.ticketChatThinkingMinuteLabel || "min";
  const minutesLabel = indicator.dataset.ticketChatThinkingMinutesLabel || minuteLabel;
  const andLabel = indicator.dataset.ticketChatThinkingAndLabel || "";

  if (totalSeconds < 60) {
    return [sinceLabel, totalSeconds, totalSeconds === 1 ? secondLabel : secondsLabel]
      .filter(String)
      .join(" ");
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const formattedMinutes = `${minutes} ${minutes === 1 ? minuteLabel : minutesLabel}`;
  if (seconds === 0) return [sinceLabel, formattedMinutes].filter(String).join(" ");

  return [
    sinceLabel,
    formattedMinutes,
    andLabel,
    seconds,
    seconds === 1 ? secondLabel : secondsLabel,
  ]
    .filter(String)
    .join(" ");
}

export default class extends Controller<HTMLElement> {
  connect(): void {
    startDocumentTicker();
    updateThinkingIndicator(this.element);
  }
}
